import { useEffect, useMemo, useState } from 'react';

import { Amount } from '@/components/common/Amount';
import { Button } from '@/components/common/Button';
import { Menu } from '@/components/common/Menu';
import { LoadingMessage } from '@/components/common/Spinner';
import { SvgAdd, SvgCheveronDown, SvgDotsVertical } from '@/components/icons';
import type { ReportData } from '@/api/types';
import { useReportsStore } from '@/store/reports';
import { theme } from '@/style/theme';
import {
  CashFlowBars,
  CategoryDonut,
  CumulativeArea,
  NetWorthArea,
} from './Charts';

function KpiCard({ widget }: { widget: ReportData }) {
  const color =
    widget.tone === 'positive' ? theme.numberPositive : theme.numberNegative;
  return (
    <section
      className="rounded-md border border-table-border bg-card-background p-4 shadow-card"
      data-testid={`kpi-${widget.id}`}
    >
      <h3 className="text-md font-medium text-page-text">{widget.label}</h3>
      <p className="pb-2 text-sm text-page-text-subdued">{widget.rangeLabel}</p>
      <div
        className="tnum truncate text-[clamp(26px,2.6vw,44px)] font-semibold leading-tight"
        style={{ color }}
      >
        <Amount value={widget.value} />
      </div>
    </section>
  );
}

function ChartCard({
  widget,
  editing,
  onRemove,
}: {
  widget: ReportData;
  editing: boolean;
  onRemove: () => void;
}) {
  const changeColor =
    (widget.change ?? 0) >= 0 ? theme.numberPositive : theme.numberNegative;

  return (
    <section
      className="flex flex-col rounded-md border border-table-border bg-card-background p-4 shadow-card"
      data-testid={`report-${widget.id}`}
    >
      <header className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-md font-medium text-page-text">{widget.label}</h3>
          <p className="text-sm text-page-text-subdued">{widget.rangeLabel}</p>
        </div>
        <div className="flex items-start gap-2">
          {widget.change !== undefined && (
            <span
              className="tnum text-sm font-medium"
              style={{ color: changeColor }}
            >
              <Amount value={widget.change} type="financial-with-sign" />
            </span>
          )}
          {widget.value !== undefined && widget.type !== 'bar' && (
            <span className="tnum text-sm font-medium text-page-text">
              <Amount value={widget.value} />
            </span>
          )}
          {editing && (
            <button
              type="button"
              onClick={onRemove}
              aria-label={`移除「${widget.label}」`}
              className="text-page-text-light hover:text-error-text"
            >
              ×
            </button>
          )}
        </div>
      </header>

      <div className="pt-3">
        {widget.type === 'bar' ? (
          <CashFlowBars data={widget} />
        ) : widget.type === 'donut' ? (
          <CategoryDonut data={widget} />
        ) : widget.id === 'net-worth' ? (
          <NetWorthArea data={widget} />
        ) : (
          <CumulativeArea data={widget} />
        )}
      </div>
    </section>
  );
}

/** Port of `components/reports/{index,ReportRouter,Overview,DashboardHeader}.tsx`. */
export function ReportsPage() {
  const widgets = useReportsStore(state => state.widgets);
  const loading = useReportsStore(state => state.loading);
  const [removed, setRemoved] = useState<string[]>([]);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    void useReportsStore.getState().load();
  }, []);

  const shown = useMemo(
    () => widgets.filter(widget => !removed.includes(widget.id)),
    [widgets, removed],
  );

  const kpis = shown.filter(widget => widget.type === 'kpi');
  const charts = shown.filter(widget => widget.type !== 'kpi');

  if (loading && widgets.length === 0) {
    return <LoadingMessage message="正在加载报表…" />;
  }

  const addable = widgets.filter(
    widget => widget.type !== 'kpi' && removed.includes(widget.id),
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="reports-page">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 px-5 pt-1">
        <h1 className="text-[25px] font-medium text-page-text">报表：主视图</h1>
        <div className="flex items-center gap-2" data-testid="dashboard-header">
          <Menu
            width={160}
            button={
              <span className="inline-flex">
                <Button size="sm" data-testid="dashboard-select">
                  主视图
                  <SvgCheveronDown width={11} height={11} />
                </Button>
              </span>
            }
            items={[{ label: '主视图', disabled: true }]}
          />
          <span className="h-5 w-px bg-menu-border" />
          <Menu
            width={210}
            data-testid="add-widget-menu"
            button={
              <span className="inline-flex">
                <Button
                  size="sm"
                  variant="primary"
                  icon={<SvgAdd width={12} height={12} />}
                >
                  新增卡片
                </Button>
              </span>
            }
            items={
              addable.length
                ? addable.map(widget => ({
                    label: widget.label,
                    onSelect: () =>
                      setRemoved(list =>
                        list.filter(id => id !== widget.id),
                      ),
                  }))
                : [{ label: '该仪表盘已包含全部卡片', disabled: true }]
            }
          />
          <Button
            size="sm"
            onClick={() => setEditing(value => !value)}
            data-testid="edit-dashboard"
          >
            {editing ? '完成' : '编辑仪表盘'}
          </Button>
          <Menu
            width={190}
            button={
              <span className="inline-flex cursor-pointer rounded-sm p-1.5 text-page-text-light hover:bg-button-bare-background-hover">
                <SvgDotsVertical width={15} height={15} />
              </span>
            }
            items={[
              {
                label: '重置仪表盘',
                onSelect: () => setRemoved([]),
              },
            ]}
          />
        </div>
      </div>

      <div className="scrollable min-h-0 flex-1 overflow-auto px-5 py-3">
        <div className="grid gap-4 small:grid-cols-2 wide:grid-cols-4">
          {kpis.map(widget => (
            <KpiCard key={widget.id} widget={widget} />
          ))}
        </div>

        <div className="grid gap-4 pt-4 small:grid-cols-2">
          {charts.map(widget => (
            <ChartCard
              key={widget.id}
              widget={widget}
              editing={editing}
              onRemove={() => setRemoved(list => [...list, widget.id])}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
